import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import type NetworkStatus from '../services/network-status';
import Icon from './icon';
import AlertTriangleIcon from '~icons/lucide/alert-triangle';
import WifiOffIcon from '~icons/lucide/wifi-off';
import RefreshCwIcon from '~icons/lucide/refresh-cw';
import HomeIcon from '~icons/lucide/home';

interface Signature {
  Args: {
    title?: string;
    message?: string;
    backRoute?: string;
    backLabel?: string;
  };
}

export default class PageError extends Component<Signature> {
  @service declare networkStatus: NetworkStatus;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    const unsubscribe = this.networkStatus.onReconnect(() => {
      this.retry();
    });
    registerDestructor(this, unsubscribe);
  }

  @action retry(): void {
    // eslint-disable-next-line warp-drive/no-legacy-request-patterns
    globalThis.location.reload();
  }

  <template>
    <div class="flex min-h-[50vh] flex-col items-center justify-center py-12">
      {{#if this.networkStatus.isOnline}}
        <div class="text-error mb-4">
          <Icon @icon={{AlertTriangleIcon}} class="inline-block h-16 w-16" />
        </div>

        <h1 class="mb-2 text-2xl font-bold">
          {{if @title @title "Something went wrong"}}
        </h1>

        <p class="text-base-content/60 mb-6 max-w-md text-center">
          {{if
            @message
            @message
            "We couldn't load this page. The server may be unavailable or there was a network error."
          }}
        </p>

        <div class="flex gap-3">
          <button
            type="button"
            class="btn btn-primary gap-2"
            {{on "click" this.retry}}
          >
            <Icon @icon={{RefreshCwIcon}} class="h-4 w-4" />
            Try again
          </button>

          {{#if @backRoute}}
            <LinkTo @route={{@backRoute}} class="btn btn-ghost gap-2">
              {{if @backLabel @backLabel "Go back"}}
            </LinkTo>
          {{else}}
            <LinkTo @route="index" class="btn btn-ghost gap-2">
              <Icon @icon={{HomeIcon}} class="h-4 w-4" />
              Home
            </LinkTo>
          {{/if}}
        </div>
      {{else}}
        <div class="text-warning mb-4">
          <Icon @icon={{WifiOffIcon}} class="inline-block h-16 w-16" />
        </div>

        <h1 class="mb-2 text-2xl font-bold">You're offline</h1>

        <p class="text-base-content/60 mb-6 max-w-md text-center">
          This page isn't available offline. Check your connection and try
          again.
        </p>

        <div class="flex gap-3">
          <LinkTo @route="index" class="btn btn-primary gap-2">
            <Icon @icon={{HomeIcon}} class="h-4 w-4" />
            Home
          </LinkTo>
        </div>
      {{/if}}
    </div>
  </template>
}
